import { createContext, useContext, useEffect, useMemo, useState } from "react";

const KEY = "uu_access_prefs";
const DEFAULTS = { textSize: "normal", contrast: false, reduceMotion: false, visualAlerts: true };

const AccessCtx = createContext({ prefs: DEFAULTS, setPref: () => {}, announce: () => {} });

export const useAccess = () => useContext(AccessCtx);

export const AccessProvider = ({ children }) => {
  const [prefs, setPrefs] = useState(() => {
    try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) || "{}") }; }
    catch { return DEFAULTS; }
  });
  const [message, setMessage] = useState("");

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(prefs));
    const root = document.documentElement;
    root.dataset.textSize = prefs.textSize;
    root.dataset.contrast = prefs.contrast ? "high" : "normal";
    root.dataset.reduceMotion = prefs.reduceMotion ? "yes" : "no";
  }, [prefs]);

  const value = useMemo(() => ({
    prefs,
    setPref: (key, val) => setPrefs((p) => ({ ...p, [key]: val })),
    /** Say something out loud to a screen reader, and flash it for anyone who cannot hear. */
    announce: (text) => { setMessage(""); setTimeout(() => setMessage(text), 60); },
  }), [prefs]);

  return (
    <AccessCtx.Provider value={value}>
      {children}
      <div aria-live="assertive" aria-atomic="true" className="sr-only" data-testid="live-region">
        {message}
      </div>
      {prefs.visualAlerts && message && (
        <div role="status" data-testid="visual-alert"
          className="pointer-events-none fixed left-0 right-0 top-0 z-[80] border-b-4 border-[#B3241B] bg-[#FFE8E6] px-4 py-3 text-center text-base font-bold text-[#8E1C15]">
          {message}
        </div>
      )}
    </AccessCtx.Provider>
  );
};
